import { useCallback, useState } from "react";
import { gql, useMutation } from "@apollo/client";
import { useApolloClients } from "~/components/ApolloClients";
import type { UploadedFileRef } from "../types";

/**
 * Obtain a presigned S3 PUT URL plus a short-lived presigned GET (`downloadUrl`)
 * the AI services later use to read the file. The shared `CreateUploadRequest`
 * document doesn't select `downloadUrl`, so we use our own here.
 */
const CREATE_UPLOAD_REQUEST = gql`
  mutation CreateUploadRequestForFile($input: CreateUploadRequestInput!) {
    createUploadRequest(input: $input) {
      id
      uploadUrl
      downloadUrl
    }
  }
`;

/**
 * Generic "upload a File to S3 and get back a reference" hook, used by the AI
 * flows that pass an uploaded file to the LLM (position description, transcript).
 */
export function useS3FileUpload() {
  const { federationGatewayClient } = useApolloClients();
  const [createUploadRequest] = useMutation(CREATE_UPLOAD_REQUEST, {
    client: federationGatewayClient,
  });
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uploadFile = useCallback(
    async (file: File): Promise<UploadedFileRef | null> => {
      setIsUploading(true);
      setError(null);
      const contentType = file.type || "application/octet-stream";
      try {
        const { data } = await createUploadRequest({
          variables: {
            input: {
              contentType,
              contentLength: file.size,
              fileName: file.name,
            },
          },
        });
        const req = data?.createUploadRequest;
        if (!req?.uploadUrl || !req?.downloadUrl) {
          throw new Error("Failed to create upload request");
        }
        const putRes = await fetch(req.uploadUrl, {
          method: "PUT",
          body: file,
          // Content-Length is a forbidden header in browsers; let fetch set it.
          headers: { "Content-Type": contentType },
        });
        if (!putRes.ok) {
          throw new Error(`Upload failed (${putRes.status})`);
        }
        return {
          key: req.id,
          downloadUrl: req.downloadUrl,
          fileName: file.name,
          contentType,
        };
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to upload file");
        return null;
      } finally {
        setIsUploading(false);
      }
    },
    [createUploadRequest],
  );

  const clearError = useCallback(() => setError(null), []);

  return { uploadFile, isUploading, error, clearError };
}
